<template>
  <section>
    <!-- Header -->

    <slot name="header" />

    <!-- Sortowanie -->

    <div class="filter-box sort-box">
      <p>
        {{ $t('filters.sorting') }}
      </p>
      <b-dropdown
        v-model="model.sort"
        aria-role="list"
        position="is-bottom-left"
        @change="x => changeQuery('sort', x)"
      >
        <template #trigger>
          <p
            class="inline-link"
            role="button"
          >
            <span v-if="model.sort">
              {{ sortName }}
            </span>
            <span v-else>
              {{ $t('filters.sortOptions.default') }}
            </span>
          </p>
        </template>

        <b-dropdown-item
          v-for="option in sortOptions"
          :key="option.label"
          :value="option.value"
          aria-role="listitem"
        >
          {{ option.label }}
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <!-- Kategorie -->

    <div v-if="categories && categories.length" class="filter-box filter-categories">
      <b-field :label="$t('filters.subcategories')">
        <div>
          <div v-if="parentCategory" class="filter-parent-category">
            {{ $t('filters.goBack') }}
            <nuxt-link
              :to="localePath({
                name: `products${filterPage ? '-' + filterPage : ''}-category`,
                params: {
                  categoryName: parentCategory.slug || toSlugTitle(parentCategory),
                  categoryId: parentCategory.id
                },
                query:queryToRouteCategories
              })"
            >
              {{ parentCategory.name }}
            </nuxt-link>
          </div>
          <ul>
            <li v-for="category in categories" :key="category.id" class="filter-category-row">
              <nuxt-link
                :to="localePath({
                  name: `products${filterPage ? '-' + filterPage : ''}-category`,
                  params: {
                    categoryName: category.slug || toSlugTitle(category),
                    categoryId: category.id
                  },
                  query: queryToRouteCategories
                })"
              >
                {{ category.name }}
              </nuxt-link>
              <span v-if="category.quantity" class="filter-quantity">
                {{ category.quantity }}
              </span>
            </li>
          </ul>
        </div>
      </b-field>
    </div>

    <!-- Cena -->

    <div class="filter-box">
      <div class="filter-price" style="align-items: flex-start;">
        <b-field :label="$t('filters.price', { currency: _currency })" />
        <span
          v-if="model.priceMin || model.priceMax"
          class="inline-link"
          @click="clearPrices()"
        >
          {{ $t('actions.clear') }}
        </span>
      </div>

      <div class="filter-price">
        <b-field>
          <price-minimal v-model="model.priceMin" :placeholder="$t('fields.from')" @changeByUser="changePrice()" />
        </b-field>
        <span>-</span>
        <b-field>
          <price-minimal v-model="model.priceMax" :placeholder="$t('fields.to')" @changeByUser="changePrice()" />
        </b-field>
      </div>

      <div v-if="priceError" class="filter-error">
        {{ $t('filters.priceError') }}
      </div>
    </div>

    <!-- Ocena -->

    <div class="filter-box">
      <b-field :label="$t('filters.opinion')">
        <b-rate v-model="model.rate" size="is-medium" @input="changeRate" />
      </b-field>
    </div>

    <!-- Filtry -->

    <div v-for="filter in options" :key="filter.id" class="filter-box">
      <b-field :label="filter.name" />
      <div class="filter-options">
        <b-field v-for="option in filter.options" :key="option.id" class="filter-option">
          <b-checkbox v-model="model.options" :native-value="filter.id + '-' + option.id" @input="x => changeQuery('options', x)">
            {{ option.name }}
          </b-checkbox>
          <span class="filter-quantity">
            {{ option.quantity }}
          </span>
        </b-field>
      </div>
    </div>

    <!-- Button -->

    <div class="filter-section">
      <button
        class="button is-primary is-outlined is-fullwidth"
        :disabled="!(model.options.length || model.priceMin || model.pirceMax || model.rate || model.sort)"
        @click="clearFilters()"
      >
        {{ $t('filters.clearFilters') }}
      </button>
    </div>

    <!-- Close -->

    <slot name="close" />
  </section>
</template>

<script>
import PriceMinimal from '~/marketplace-front-core/components/form/priceMinimal'
import debounce from '~/marketplace-front-core/utils/debounce'

export default {
  components: {
    PriceMinimal
  },
  props: {
    categories: {
      type: Array,
      default () {
        return []
      }
    },
    currentCategory: {
      type: Object,
      default: null
    },
    parentCategory: {
      type: Object,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    filterPage: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      model: {
        options: [],
        priceMin: null,
        priceMax: null,
        rate: null,
        sort: null
      },
      lastRate: null,
      priceError: false,
      sortOptions: [
        {
          label: this.$t('filters.sortOptions.priceASC'),
          value: ['price', 'ASC']
        },
        {
          label: this.$t('filters.sortOptions.priceDESC'),
          value: ['price', 'DESC']
        },
        {
          label: this.$t('filters.sortOptions.starASC'),
          value: ['star', 'ASC']
        },
        {
          label: this.$t('filters.sortOptions.starDESC'),
          value: ['star', 'DESC']
        }
      ]
    }
  },
  computed: {
    sortName () {
      if (this.model.sort && Array.isArray(this.model.sort)) {
        const sorted = this.sortOptions.find((el) => {
          return JSON.stringify(el.value) === JSON.stringify(this.model.sort)
        }).label
        return sorted
      } else {
        return this.$t('filters.sortOptions.priceASC')
      }
    },
    queryToRouteCategories () {
      const query = this._clone(this.$route.query)
      delete query.page
      return query
    }
  },
  watch: {
    '$route.query.options': {
      handler (value) {
        if (Array.isArray(value)) {
          this.model.options = value
        } else {
          this.model.options = [value]
        }
      },
      immediate: true
    },
    '$route.query.priceMin': {
      handler (value) {
        this.model.priceMin = value ? Number(value) : null
      },
      immediate: true
    },
    '$route.query.priceMax': {
      handler (value) {
        this.model.priceMax = value ? Number(value) : null
      },
      immediate: true
    },
    '$route.query.sort': {
      handler (value) {
        if (Array.isArray(value)) {
          const find = this.sortOptions.find(x => x.value[0] === value[0] && x.value[1] === value[1])
          this.model.sort = find ? find.value : null
        } else {
          this.model.sort = null
        }
      },
      immediate: true
    },
    '$route.query.rate': {
      handler (value) {
        if (value) {
          this.model.rate = parseInt(value)
          this.lastRate = parseInt(value)
        } else {
          this.model.rate = null
          this.lastRate = null
        }
      },
      immediate: true
    }

  },
  methods: {
    changeRate (newValue) {
      if (this.model.rate === this.lastRate) {
        this.model.rate = null
        this.changeQuery('rate', null)
      } else {
        this.changeQuery('rate', newValue)
      }
    },
    changeQueryFunction (query, key, value) {
      if (value) {
        query[key] = value
      } else {
        delete query[key]
      }

      return query
    },
    changeQuery (key, value) {
      let query = this._clone(this.$route.query)
      delete query.page

      query = this.changeQueryFunction(query, key, value)

      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        this.$router.push({ query })
      }
    },
    changePrice: debounce(function () {
      if (!this.model.priceMax || this.model.priceMax >= this.model.priceMin) {
        this.priceError = false

        let query = this._clone(this.$route.query)
        delete query.page

        query = this.changeQueryFunction(query, 'priceMin', this.model.priceMin)
        query = this.changeQueryFunction(query, 'priceMax', this.model.priceMax)

        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          this.$router.push({ query })
        }
      } else {
        this.priceError = true
      }
    }),
    clearFilters () {
      this.model = {
        options: [],
        priceMin: null,
        priceMax: null,
        rate: null,
        sort: null
      }

      this.lastRate = null
      this.priceError = false

      const query = this._clone(this.$route.query)
      delete query.page
      delete query.priceMin
      delete query.priceMax
      delete query.options
      delete query.rate
      delete query.sort

      this.$router.push({ query })
    },
    clearPrices () {
      const query = this._clone(this.$route.query)
      delete query.priceMin
      delete query.priceMax
      this.$router.push({ query })
      this.model.priceMin = null
      this.model.priceMax = null
      this.priceError = false
    }
  }
}
</script>
